<script setup lang="ts">
import { onMounted, ref } from "vue";

import AdminShell from "../components/AdminShell.vue";
import StatusBadge from "../components/StatusBadge.vue";
import { adminApi, type OrderListItem } from "../lib/api";

const orders = ref<OrderListItem[]>([]);
const search = ref("");
const status = ref("");
const cursor = ref<string | null>(null);
const loading = ref(true);
const loadingMore = ref(false);
const error = ref("");

async function load(reset = true) {
  if (reset) {
    loading.value = true;
    cursor.value = null;
  } else loadingMore.value = true;
  error.value = "";
  try {
    const response = await adminApi.listOrders({
      query: search.value.trim() || undefined,
      status: status.value || undefined,
      cursor: reset ? undefined : cursor.value || undefined
    });
    orders.value = reset ? response.orders : [...orders.value, ...response.orders];
    cursor.value = response.nextCursor;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "目前無法讀取訂單。";
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-TW", {
    month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false
  }).format(new Date(value));
}

function money(value: number) {
  return new Intl.NumberFormat("zh-TW", { style: "currency", currency: "TWD", maximumFractionDigits: 0 }).format(value);
}

onMounted(() => load());
</script>

<template>
  <AdminShell>
    <header class="page-head">
      <div><p class="eyebrow">Orders & delivery</p><h1>訂單與報告</h1></div>
      <p class="page-purpose">從款項進來，到 PDF 交付完成。</p>
    </header>

    <section class="flow-strip" aria-label="訂單流程">
      <span><b>01</b> 建立訂單</span><i></i><span><b>02</b> 確認付款</span><i></i>
      <span><b>03</b> 產生報告</span><i></i><span><b>04</b> PDF 可下載</span>
    </section>

    <section class="order-workspace">
      <form class="filter-bar" @submit.prevent="load(true)">
        <label class="search-field">
          <span class="sr-only">搜尋訂單</span>
          <input v-model="search" placeholder="訂單編號、付款編號或用戶 UID" />
        </label>
        <label>
          <span class="sr-only">訂單狀態</span>
          <select v-model="status" @change="load(true)">
            <option value="">所有狀態</option>
            <option value="payment_requested">等待付款</option>
            <option value="confirming">確認款項</option>
            <option value="paid">已付款</option>
            <option value="refunded">已退款</option>
            <option value="cancelled">已取消</option>
          </select>
        </label>
        <button class="primary-button compact" type="submit">查詢</button>
      </form>

      <div v-if="error" class="inline-error" role="alert">
        <span>{{ error }}</span><button type="button" @click="load(true)">再試一次</button>
      </div>
      <div v-else-if="loading" class="loading-state">正在整理訂單流向…</div>
      <div v-else-if="orders.length === 0" class="empty-state">
        <strong>沒有符合條件的訂單</strong><p>調整狀態，或輸入完整的訂單編號、付款編號、用戶 UID。</p>
      </div>
      <div v-else class="order-table-wrap">
        <table class="order-table">
          <thead><tr><th>訂單</th><th>款項</th><th>訂單狀態</th><th>報告交付</th><th>建立時間</th><th></th></tr></thead>
          <tbody>
            <tr v-for="order in orders" :key="order.orderId">
              <td><strong>{{ order.productName }}</strong><code>{{ order.orderId }}</code></td>
              <td><strong>{{ money(order.amount) }}</strong><code>{{ order.paymentTradeNo }}</code></td>
              <td><StatusBadge :status="order.status" /></td>
              <td>
                <template v-if="order.report">
                  <StatusBadge :status="order.report.status" />
                  <small v-if="order.report.status === 'generating'">{{ order.report.progress }}%</small>
                </template>
                <span v-else class="muted">尚未建立</span>
              </td>
              <td>{{ formatDate(order.createdAt) }}</td>
              <td><RouterLink class="row-link" :to="`/orders/${order.orderId}`">查看流向 <span>→</span></RouterLink></td>
            </tr>
          </tbody>
        </table>
      </div>
      <button v-if="cursor" class="load-more" type="button" :disabled="loadingMore" @click="load(false)">
        {{ loadingMore ? "讀取中…" : "載入更早訂單" }}
      </button>
    </section>
  </AdminShell>
</template>
